import React, { useState } from 'react';
import { FiX, FiUser, FiPhone, FiStar, FiBriefcase, FiTruck } from 'react-icons/fi';

export default function AssignModal({ isOpen, onClose, onAssign, type, drivers, tourGuides, tourCompanies }) {
  const [selectedId, setSelectedId] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  let items = [];
  let typeLabel = '';
  let icon = null;

  switch (type) {
    case 'driver':
      items = drivers;
      typeLabel = 'Driver';
      icon = <FiTruck className="w-5 h-5 mr-2" />;
      break;
    case 'tour_guide':
      items = tourGuides;
      typeLabel = 'Tour Guide';
      icon = <FiStar className="w-5 h-5 mr-2" />;
      break;
    case 'tour_company':
      items = tourCompanies;
      typeLabel = 'Tour Company';
      icon = <FiBriefcase className="w-5 h-5 mr-2" />;
      break;
    default:
      items = [];
      typeLabel = '';
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    
    setLoading(true);
    await onAssign({
      type,
      id: selectedId,
      cost: cost ? parseFloat(cost) : 0,
      notes: notes.trim() || undefined,
    });
    setLoading(false);
    onClose();
    setSelectedId('');
    setCost('');
    setNotes('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            {icon} Add {typeLabel}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select {typeLabel}
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2">
                {items.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No available {typeLabel}s found</p>
                ) : (
                  items.map((item) => (
                    <label
                      key={item._id}
                      className={`flex items-start p-3 rounded-lg cursor-pointer transition ${
                        selectedId === item._id
                          ? 'bg-primary-50 border-primary-500'
                          : 'hover:bg-gray-50 border-transparent'
                      } border`}
                    >
                      <input
                        type="radio"
                        name="assignee"
                        value={item._id}
                        checked={selectedId === item._id}
                        onChange={(e) => setSelectedId(e.target.value)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{item.name}</p>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <FiPhone className="w-3 h-3 mr-1" />
                          <span>{item.phone}</span>
                        </div>
                        {type === 'driver' && item.vehicle && (
                          <p className="text-xs text-gray-400 mt-1">
                            {item.vehicle.model} • {item.vehicle.license_plate}
                          </p>
                        )}
                        {type === 'tour_guide' && item.languages && (
                          <p className="text-xs text-gray-400 mt-1">
                            Languages: {item.languages.map(l => l.name).join(', ')}
                          </p>
                        )}
                        {type === 'tour_company' && item.service_types && (
                          <p className="text-xs text-gray-400 mt-1">
                            Services: {item.service_types.join(', ')}
                          </p>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cost (Optional)
              </label>
              <input
                type="number"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder={`Amount you'll pay to ${typeLabel.toLowerCase()}`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={`e.g., Special instructions for ${typeLabel.toLowerCase()}`}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedId || loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition"
            >
              {loading ? 'Adding...' : `Add ${typeLabel}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
